import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import dataService from '../services/dataService';

export type OwnedCreatorDetail = Awaited<ReturnType<typeof dataService.getCreatorDetail>>;
type State = { ownerId: string | null; profiles: OwnedCreatorDetail[]; loading: boolean; error: string | null };
const EMPTY: State = { ownerId: null, profiles: [], loading: true, error: null };
const Context = createContext<State & { refresh: () => void }>({ ...EMPTY, refresh: () => {} });

/** One account-scoped identity snapshot. A claim is NOT a live OAuth connection. */
export function OwnedCreatorProvider({ children }: { children: React.ReactNode }) {
  const { userId, isLoaded, getToken } = useAuth();
  const [state, setState] = useState<State>(EMPTY);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion(v => v + 1), []);
  useEffect(() => {
    let cancelled = false;
    setState({ ...EMPTY, ownerId: userId || null, loading: !!userId });
    if (!isLoaded || !userId) return;
    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('Sign in again to load your channels.');
        const { creators } = await dataService.getMyClaimedCreators(token);
        const profiles = await Promise.all(creators.map(c =>
          dataService.getCreatorDetail(c.platform, c.handle, { authToken: token })));
        if (!cancelled) setState({ ownerId: userId, profiles: profiles.filter(p => p.claimed_by_me), loading: false, error: null });
      } catch {
        if (!cancelled) setState({ ownerId: userId, profiles: [], loading: false, error: 'Could not load your saved channels. Please retry.' });
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, isLoaded, userId, version]);
  // Never render the previous account's snapshot, even for one render.
  const visible = state.ownerId === (userId || null) ? state : EMPTY;
  return <Context.Provider value={{ ...visible, refresh }}>{children}</Context.Provider>;
}

export function useOwnedCreator() {
  const state = useContext(Context);
  const profile = state.profiles.find(p => p.creator.platform === 'youtube') || state.profiles[0] || null;
  return { ...state, profile, isDemo: state.profiles.some(p => p.creator.is_demo) };
}

export function OwnedCreatorNotice() {
  const { isDemo, error, refresh } = useOwnedCreator();
  if (error) return <div role="alert" className="br-note br-note--amber br-note--straight">{error} <button onClick={refresh} className="br-inkline">Retry</button></div>;
  if (!isDemo) return null;
  // A slip of paper taped to the desk, stamped. The honesty label is an object
  // on the desk, not a banner painted across it.
  return <aside aria-label="Demo account" className="br-note br-note--blue">
    <strong className="br-stamp">Demo</strong><span>Fictional creator, sample figures. No real sales or sponsor endorsements.</span>
  </aside>;
}
